import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { api } from '../services/api';
import SafeIcon from '../common/SafeIcon';

const Production = () => {
  const { data, t, formatUnit } = useAppContext();
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);
  const [productionData, setProductionData] = useState({ recipes: [], unlinkedProducts: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const summary = await api.getProductionSummary(targetDate);
        // Ensure summary always has the expected structure even if backend returns null/empty
        setProductionData(summary || { recipes: [], unlinkedProducts: [] });
      } catch (err) {
        console.error("Production fetch error:", err);
        setProductionData({ recipes: [], unlinkedProducts: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [targetDate, data.orders, data.orderLines]);

  /**
   * Fixed Recipe Calculation Logic
   */
  const calculateScaledIngredients = (recipe, totalRequiredWeight) => {
    if (!recipe || !recipe.baker_percentage_notes || totalRequiredWeight <= 0) return [];
    
    try {
      let ingredients = [];
      const rawData = recipe.baker_percentage_notes;
      
      if (typeof rawData === 'string' && (rawData.startsWith('[') || rawData.startsWith('{'))) {
        ingredients = JSON.parse(rawData);
      } else if (Array.isArray(rawData)) {
        ingredients = rawData;
      }

      if (!Array.isArray(ingredients) || ingredients.length === 0) return [];

      const totalPercentage = ingredients.reduce((sum, ing) => sum + (Number(ing.percentage) || 0), 0);
      if (totalPercentage <= 0) return [];

      const flourBasis = totalRequiredWeight / (totalPercentage / 100);

      return ingredients.map(ing => ({
        ...ing,
        weight: flourBasis * (Number(ing.percentage) / 100)
      }));
    } catch (e) {
      console.error("[Production Calc Error] Failed to scale recipe:", e);
      return [];
    }
  };

  // Fixed Line 66 Crash: Safe access with optional chaining and fallbacks
  const recipes = productionData?.recipes || [];
  const unlinked = productionData?.unlinkedProducts || [];
  const hasData = (recipes.length > 0) || (unlinked.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('production.title')}</h1>
          <p className="text-gray-500 mt-1">{t('production.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
          <SafeIcon name="Calendar" className="text-gray-400 w-5 h-5" />
          <input 
            type="date" 
            value={targetDate} 
            onChange={(e) => setTargetDate(e.target.value)} 
            className="border-none bg-transparent outline-none text-gray-700 font-medium cursor-pointer"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-500">{t('production.calculating')}</div>
      ) : !hasData ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100 shadow-sm">
          <SafeIcon name="Inbox" className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">{t('production.noProduction')}</h3>
          <p className="text-gray-500">{t('production.noOrders')}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {recipes.map((group) => {
            const scaledIngredients = calculateScaledIngredients(group.recipe, group.totalDoughWeight);
            
            return (
              <div key={group.recipe?.id || Math.random()} className="bg-white rounded-xl border-2 border-amber-100 shadow-sm overflow-hidden">
                <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <h2 className="text-xl font-black text-amber-900 uppercase tracking-tight">{group.recipe?.name || 'Unknown Recipe'}</h2>
                    <p className="text-sm text-amber-700 font-medium">{t('production.dailyScaled')}</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">{t('production.requiredDough')}</p>
                      <p className="text-2xl font-black text-amber-900">{((group.totalDoughWeight || 0) / 1000).toFixed(2)}kg</p>
                    </div>
                    <div className="h-10 w-px bg-amber-200"></div>
                    <div className="text-center">
                      <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">{t('production.suggestedBatches')}</p>
                      <p className="text-2xl font-black text-amber-900">{group.batches || 0}x</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12">
                  <div className="lg:col-span-7 p-6 border-r border-gray-100">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <SafeIcon name="FileText" className="w-3 h-3" /> {t('production.ingredientScale')}
                    </h3>
                    {scaledIngredients.length > 0 ? (
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-gray-100 text-[10px] uppercase text-gray-400 font-bold">
                            <th className="pb-2">{t('print.ingredient')}</th>
                            <th className="pb-2 text-center">%</th>
                            <th className="pb-2 text-right">{t('print.weight')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {scaledIngredients.map((ing, idx) => (
                            <tr key={idx} className="group">
                              <td className="py-2 pr-2">
                                <span className="font-bold text-gray-800 text-sm">{ing.name}</span>
                                {ing.note && <p className="text-[10px] text-gray-400 italic">{ing.note}</p>}
                              </td>
                              <td className="py-2 text-center text-sm font-medium text-gray-500">{ing.percentage}%</td>
                              <td className="py-2 text-right">
                                <span className="font-mono font-black text-lg text-gray-900">
                                  {ing.weight >= 1000 ? `${(ing.weight / 1000).toFixed(2)}kg` : `${Math.round(ing.weight)}g`}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-4 text-sm text-gray-400 italic bg-gray-50 rounded-lg text-center border border-dashed border-gray-200">
                        {t('production.noPercentages')}
                      </div>
                    )}
                  </div>
                  <div className="lg:col-span-5 p-6 bg-gray-50/50">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <SafeIcon name="ShoppingCart" className="w-3 h-3" /> {t('production.contributingOrders')}
                    </h3>
                    <div className="space-y-3">
                      {(group.products || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm bg-white p-2 rounded border border-gray-100 shadow-sm">
                          <div>
                            <p className="font-bold text-gray-800">{item.product?.name || 'Unknown'}</p>
                            <p className="text-[10px] text-gray-400 uppercase font-bold">{item.qty} units × {item.product?.dough_weight || 0}g</p>
                          </div>
                          <p className="font-mono font-bold text-gray-600">{((item.lineWeight || 0) / 1000).toFixed(2)}kg</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {unlinked.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden opacity-80">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-700">{t('production.standalone')}</h2>
              </div>
              <table className="w-full text-left border-collapse">
                <tbody className="divide-y divide-gray-100">
                  {unlinked.map((item, idx) => (
                    <tr key={idx}>
                      <td className="p-4 pl-6 font-medium text-gray-800">{item.product?.name || 'Unknown'}</td>
                      <td className="p-4 text-right pr-6 font-black text-lg">{item.qty} {formatUnit(item.product?.unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Production;